/**
 * Analytics event tracking.
 *
 * Two concerns live here:
 *
 *   1. Umami funnel events — trackEvent() + FUNNEL_EVENTS.
 *   2. GA4 + Google Ads conversion events — trackSignup/trackBeginTrial/trackPurchase.
 *
 * Google Consent Mode v2 defaults are set in public/google-analytics-bootstrap.js.
 * Consent updates flow through src/lib/cookieConsent.ts → gtag('consent', 'update'),
 * and gate which events will actually reach Google.
 */

import {
  CONVERSION_LABELS,
  GOOGLE_ADS_ID,
  PURCHASE_WEB_SEND_TO,
  SIGNUP_SEND_TO,
} from '@/config/analytics';

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, string | number | boolean>) => void;
    };
    __fabAnalytics?: {
      trackSignup: typeof trackSignup;
      trackBeginTrial: typeof trackBeginTrial;
      trackPurchase: typeof trackPurchase;
      hashEmail: typeof hashEmailForEnhancedConversions;
    };
  }
}

// ---------------------------------------------------------------------------
// Umami (existing)
// ---------------------------------------------------------------------------

export function trackEvent(event: string, data?: Record<string, string | number | boolean>) {
  try {
    window.umami?.track(event, data);
  } catch {
    // Silently ignore — analytics should never break the app
  }
}

export const FUNNEL_EVENTS = {
  SIGNUP_START: 'signup-start',
  SIGNUP_SUCCESS: 'signup-success',
  CHECKOUT_START: 'checkout-start',
  CHECKOUT_SUCCESS: 'checkout-success',
  FREE_TIER_ACTIVATED: 'free-tier-activated',
  PAID_ACTIVATION: 'paid-activation',
} as const;

// ---------------------------------------------------------------------------
// Google tag (GA4 + Google Ads)
// ---------------------------------------------------------------------------

type ConsentKind = 'ads' | 'analytics';

export function consentAllows(kind: ConsentKind): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (kind === 'ads') {
    return window.__fabGaAdvertisingConsentGranted === true;
  }
  return window.__fabGaAnalyticsConsentGranted === true;
}

/**
 * True only when the Google Ads tag has been explicitly configured for THIS
 * account via configureGoogleAdsTag() (called from main.tsx only when
 * detectEnvironment() === 'production'). Without this gate, gtag's send_to
 * parameter would auto-load the AW tag on first use, which would leak
 * dev/staging conversion pings into the production Ads account.
 */
function adsTagInstalled(): boolean {
  return typeof window !== 'undefined' && window.__fabGoogleAdsTagConfigured === GOOGLE_ADS_ID;
}

/**
 * Dev-only diagnostic: a Google Ads conversion was skipped. The common,
 * acceptable case is the user signing up without accepting advertising
 * cookies — we intentionally lose that one conversion. Logged only in DEV so
 * it never reaches production consoles.
 */
function warnDroppedConversion(event: 'sign_up' | 'purchase'): void {
  if (!import.meta.env.DEV) {
    return;
  }
  let reason: string;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    reason = 'window.gtag is unavailable (bootstrap not loaded)';
  } else if (!consentAllows('ads')) {
    reason = 'advertising consent not granted';
  } else {
    reason = 'Google Ads tag not installed (non-production build)';
  }
  console.warn(
    `[analytics] ${event} Google Ads conversion skipped: ${reason}. ` +
      'GA4 event still sent if analytics consent was granted.',
  );
}

export function safeGtag(...args: unknown[]): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  try {
    window.gtag(...args);
  } catch {
    // Never let analytics break the app
  }
}

/**
 * Hash an email for Google Ads Enhanced Conversions per Google spec:
 * lowercase, trim whitespace, SHA-256, hex-encoded.
 *
 * Test vectors from Google's spec:
 *   "example@gmail.com" → "31c5543c1734d25c7206f5fd591525d0295bec6fe84ff82f946a34fe970a1e66"
 *   "  EXAMPLE@gmail.com " → same as above (normalizes)
 */
export async function hashEmailForEnhancedConversions(email: string): Promise<string> {
  const normalized = email.trim().toLowerCase();
  const bytes = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Attach user-provided data to subsequent Enhanced Conversions events.
 * Must be called before the relevant Google Ads conversion gtag event fires.
 *
 * We pass the RAW email under the `email` key. gtag.js normalizes (trim +
 * lowercase) and SHA-256 hashes it client-side before transmission.
 *
 * The previous implementation sent a pre-hashed value under the key
 * `sha256_email`. That is not a field gtag's Enhanced Conversions reader
 * recognizes — the documented hashed key is `sha256_email_address`, and the
 * raw key is `email` — so the data was silently dropped and Google Ads
 * reported "Enhanced conversions setup is not active". Kept async so existing
 * `await` call sites are unaffected.
 */
export async function setEnhancedConversionsUserData(email: string): Promise<void> {
  if (!consentAllows('ads') || !adsTagInstalled()) {
    return;
  }
  const normalizedEmail = email.trim();
  if (!normalizedEmail) {
    return;
  }
  safeGtag('set', 'user_data', { email: normalizedEmail });
}

/**
 * Fire the sign_up conversion — GA4 event + Google Ads website conversion.
 * If email is supplied, hashed user data is set first so Enhanced Conversions
 * can match.
 * Call this from /welcome on first render when profiles.signup_tracked_at IS NULL.
 */
export async function trackSignup(email?: string): Promise<void> {
  if (consentAllows('analytics')) {
    safeGtag('event', 'sign_up', { method: 'email' });
  }

  if (
    typeof window === 'undefined' ||
    typeof window.gtag !== 'function' ||
    !consentAllows('ads') ||
    !adsTagInstalled()
  ) {
    warnDroppedConversion('sign_up');
    return;
  }

  if (email) {
    await setEnhancedConversionsUserData(email);
  }

  safeGtag('event', 'conversion', { send_to: SIGNUP_SEND_TO });
}

type TrialPayload = {
  value: number;
  currency: string;
};

/**
 * Fire the begin_trial GA4 event. No client-side Google Ads conversion —
 * the Ads signal for trial start is uploaded server-side via the webhook
 * so we have gclid attribution even when the user is not in the tab.
 */
export function trackBeginTrial(payload: TrialPayload): void {
  if (consentAllows('analytics')) {
    safeGtag('event', 'begin_trial', {
      value: payload.value,
      currency: payload.currency,
    });
  }
}

type PurchasePayload = {
  value: number;
  currency: string;
  transactionId: string;
  /** Optional — provided enables Enhanced Conversions on the Ads side. */
  email?: string;
};

/**
 * Fire the purchase conversion — GA4 purchase event + Google Ads website
 * conversion. If email is supplied, hashed user data is set first so
 * Enhanced Conversions can match.
 *
 * For trial-to-paid purchases, this is complemented by a server-side upload
 * from the Stripe invoice.paid webhook (PR 2). That path is the canonical
 * one; the client-side fire here covers non-trial flows (Starter $20 one-time).
 */
export async function trackPurchase(payload: PurchasePayload): Promise<void> {
  if (consentAllows('analytics')) {
    safeGtag('event', 'purchase', {
      transaction_id: payload.transactionId,
      value: payload.value,
      currency: payload.currency,
    });
  }

  if (
    typeof window === 'undefined' ||
    typeof window.gtag !== 'function' ||
    !consentAllows('ads') ||
    !adsTagInstalled()
  ) {
    warnDroppedConversion('purchase');
    return;
  }

  if (payload.email) {
    await setEnhancedConversionsUserData(payload.email);
  }

  safeGtag('event', 'conversion', {
    send_to: PURCHASE_WEB_SEND_TO,
    value: payload.value,
    currency: payload.currency,
    transaction_id: payload.transactionId,
  });
}

// ---------------------------------------------------------------------------
// GA4 custom engagement events (landing pages)
// ---------------------------------------------------------------------------

/**
 * Fire a GA4 `cta_click` event for a marketing/landing-page call to action.
 * Analytics-consent-gated, like page_view — never a Google Ads conversion.
 */
export function trackCtaClick(location: string, destination: string): void {
  if (consentAllows('analytics')) {
    safeGtag('event', 'cta_click', { location, destination });
  }
}

/**
 * Fire a GA4 `lead_submit` event when a gated-resource form is submitted.
 * Analytics-consent-gated. The Google Ads sign-up conversion is intentionally
 * NOT fired for lead-gen (see resources pages — left commented with a TODO).
 */
export function trackLeadSubmit(asset: string): void {
  if (consentAllows('analytics')) {
    safeGtag('event', 'lead_submit', { asset });
  }
}

// ---------------------------------------------------------------------------
// Dev-only test bench
// ---------------------------------------------------------------------------

if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__fabAnalytics = {
    trackSignup,
    trackBeginTrial,
    trackPurchase,
    hashEmail: hashEmailForEnhancedConversions,
  };
}

// Re-export for consumers that want to know conversion identifiers directly.
export { CONVERSION_LABELS, GOOGLE_ADS_ID };
